import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  InputAdornment,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { SelectedMeeting } from "../../pages/notes/meetingNotes";
import {
  formatLocalDateDisplay,
  formatMeetingTimeRange,
  parseLocalDateValue,
} from "../../util/meetingDateTime";

interface EditableMeetingInfoProps {
  meeting: SelectedMeeting;
  onMeetingChange: (meeting: SelectedMeeting) => void;
  layout?: "compact" | "stacked" | "icon-only";
}

const parseDate = (dateStr: string): Dayjs | null => {
  const parsedDate = parseLocalDateValue(dateStr);
  const d = parsedDate ? dayjs(parsedDate) : dayjs(dateStr);
  return d.isValid() ? d : null;
};

const formatDateISO = (date: Dayjs): string => {
  return date.format("YYYY-MM-DD");
};

const parseTime = (timeStr: string): Dayjs | null => {
  const normalized = timeStr.trim();
  if (!normalized) {
    return null;
  }

  const match = /^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/.exec(normalized);
  if (!match) {
    return null;
  }

  const [, rawHour, rawMinute, rawPeriod] = match;
  const minute = Number(rawMinute);
  let hour = Number(rawHour);

  if (Number.isNaN(hour) || Number.isNaN(minute) || minute > 59) {
    return null;
  }

  if (rawPeriod) {
    if (hour < 1 || hour > 12) {
      return null;
    }

    const period = rawPeriod.toUpperCase();
    if (period === "AM") {
      hour = hour === 12 ? 0 : hour;
    } else {
      hour = hour === 12 ? 12 : hour + 12;
    }
  } else if (hour > 23) {
    return null;
  }

  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

const formatTime = (
  value: Dayjs | null,
  useAmPm: boolean,
  locale?: string,
): string => {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat(useAmPm ? "en-US" : locale || undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: useAmPm,
  }).format(value.toDate());
};

const EditableMeetingInfo = ({
  meeting,
  onMeetingChange,
  layout = "stacked",
}: EditableMeetingInfoProps) => {
  const { t, i18n } = useTranslation();
  const useAmPm = i18n.language?.toLowerCase().startsWith("en") ?? false;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDate, setEditDate] = useState<Dayjs | null>(null);
  const [editStartTime, setEditStartTime] = useState<Dayjs | null>(null);
  const [editEndTime, setEditEndTime] = useState<Dayjs | null>(null);
  const [editParticipants, setEditParticipants] = useState<string[]>([]);

  const openDialog = () => {
    setEditTitle(meeting.title);
    setEditDate(parseDate(meeting.date));
    setEditStartTime(parseTime(meeting.startTime));
    setEditEndTime(parseTime(meeting.endTime));
    setEditParticipants(meeting.originalMeeting?.participants || []);
    setDialogOpen(true);
  };

  const handleSave = () => {
    onMeetingChange({
      ...meeting,
      title: editTitle.trim() || meeting.title,
      date: editDate ? formatDateISO(editDate) : meeting.date,
      startTime:
        formatTime(editStartTime, useAmPm, i18n.language) || meeting.startTime,
      endTime:
        formatTime(editEndTime, useAmPm, i18n.language) || meeting.endTime,
      attendees: editParticipants.length,
      originalMeeting: meeting.originalMeeting
        ? { ...meeting.originalMeeting, participants: editParticipants }
        : meeting.originalMeeting,
    });
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  if (layout === "icon-only") {
    return (
      <>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            openDialog();
          }}
          size="small"
          sx={{
            color: "#64748B",
            flexShrink: 0,
            "&:hover": { backgroundColor: "#EDF0F4", color: "#242C63" },
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <EditDialog
          open={dialogOpen}
          title={editTitle}
          date={editDate}
          startTime={editStartTime}
          endTime={editEndTime}
          participants={editParticipants}
          onTitleChange={setEditTitle}
          onDateChange={setEditDate}
          onStartTimeChange={setEditStartTime}
          onEndTimeChange={setEditEndTime}
          onParticipantsChange={setEditParticipants}
          useAmPm={useAmPm}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </>
    );
  }

  if (layout === "compact") {
    return (
      <>
        <Box
          onClick={openDialog}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            cursor: "pointer",
            borderRadius: "4px",
            px: 0.5,
            mx: -0.5,
            py: 0.25,
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: "#EDF0F4" },
            "&:hover .edit-icon": { opacity: 1 },
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "#242C63",
            }}
          >
            {meeting.title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <EventOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
            <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
              {formatLocalDateDisplay(meeting.date)}
            </Typography>
          </Box>
          {meeting.startTime || meeting.endTime ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
              <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                {formatMeetingTimeRange(meeting.startTime, meeting.endTime)}
              </Typography>
            </Box>
          ) : null}
          {meeting.originalMeeting?.participants &&
          meeting.originalMeeting.participants.length > 0 ? (
            <Tooltip
              title={meeting.originalMeeting.participants.join(", ")}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "default",
                }}
              >
                <PeopleOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                  {meeting.originalMeeting.participants.length}
                </Typography>
              </Box>
            </Tooltip>
          ) : null}
          <EditOutlinedIcon
            className="edit-icon"
            sx={{
              fontSize: 14,
              color: "#9CA3AF",
              opacity: 0,
              transition: "opacity 0.15s ease",
              ml: 0.5,
            }}
          />
        </Box>
        <EditDialog
          open={dialogOpen}
          title={editTitle}
          date={editDate}
          startTime={editStartTime}
          endTime={editEndTime}
          participants={editParticipants}
          onTitleChange={setEditTitle}
          onDateChange={setEditDate}
          onStartTimeChange={setEditStartTime}
          onEndTimeChange={setEditEndTime}
          onParticipantsChange={setEditParticipants}
          useAmPm={useAmPm}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </>
    );
  }

  // Stacked layout for step 1
  return (
    <>
      <Box
        onClick={openDialog}
        sx={{
          cursor: "pointer",
          borderRadius: "4px",
          px: 1,
          mx: -1,
          py: 0.5,
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "#EDF0F4" },
          "&:hover .edit-icon": { opacity: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "#1A2048",
            }}
          >
            {meeting.title}
          </Typography>
          <EditOutlinedIcon
            className="edit-icon"
            sx={{
              fontSize: 14,
              color: "#9CA3AF",
              ml: 0.25,
              opacity: 0,
              transition: "opacity 0.15s ease",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <EventOutlinedIcon sx={{ fontSize: 15, color: "#64748B" }} />
            <Typography sx={{ fontSize: "0.8125rem", color: "#64748B" }}>
              {formatLocalDateDisplay(meeting.date)}
            </Typography>
          </Box>
          {meeting.startTime || meeting.endTime ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 15, color: "#64748B" }} />
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748B" }}>
                {formatMeetingTimeRange(meeting.startTime, meeting.endTime)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
      <EditDialog
        open={dialogOpen}
        title={editTitle}
        date={editDate}
        startTime={editStartTime}
        endTime={editEndTime}
        participants={editParticipants}
        onTitleChange={setEditTitle}
        onDateChange={setEditDate}
        onStartTimeChange={setEditStartTime}
        onEndTimeChange={setEditEndTime}
        onParticipantsChange={setEditParticipants}
        useAmPm={useAmPm}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </>
  );
};

interface EditDialogProps {
  open: boolean;
  title: string;
  date: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  participants: string[];
  onTitleChange: (v: string) => void;
  onDateChange: (v: Dayjs | null) => void;
  onStartTimeChange: (v: Dayjs | null) => void;
  onEndTimeChange: (v: Dayjs | null) => void;
  onParticipantsChange: (v: string[]) => void;
  useAmPm: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const EditDialog = ({
  open,
  title,
  date,
  startTime,
  endTime,
  participants,
  onTitleChange,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onParticipantsChange,
  useAmPm,
  onSave,
  onCancel,
}: EditDialogProps) => {
  const { t } = useTranslation();
  const [newParticipant, setNewParticipant] = useState("");
  const [participantError, setParticipantError] = useState<
    "duplicate" | null
  >(null);

  const trimmedParticipant = newParticipant.trim();
  const isDuplicate = participants.some(
    (p) => p.toLowerCase() === trimmedParticipant.toLowerCase(),
  );

  const handleAddParticipant = () => {
    if (!trimmedParticipant) {
      return;
    }
    if (isDuplicate) {
      setParticipantError("duplicate");
      return;
    }
    onParticipantsChange([...participants, trimmedParticipant]);
    setNewParticipant("");
    setParticipantError(null);
  };

  const handleRemoveParticipant = (email: string) => {
    onParticipantsChange(participants.filter((p) => p !== email));
  };
  const timeInputSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.875rem",
      borderRadius: "4px",
      backgroundColor: "#FFFFFF",
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#242C63" },
      "&.Mui-focused fieldset": {
        borderColor: "#242C63",
        boxShadow: "0 0 0 3px rgba(227,216,191,0.4)",
      },
    },
  };

  const timePickerSlotProps = {
    textField: {
      fullWidth: true,
      size: "small" as const,
      sx: timeInputSx,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="xs" slotProps={{ paper: {
        sx: {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0px 16px 40px -8px rgba(36, 44, 99, 0.12)",
        },
      } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
          py: 1.5,
          px: 3,
          fontSize: "1rem",
          fontWeight: 600,
          color: "#242C63",
        }}
      >
        {t("meetingNotes.editableMeetingInfo.dialogTitle")}
        <IconButton onClick={onCancel} size="small" sx={{ color: "#64748B" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Title field */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
                pt: 1,
              }}
            >
              {t("meetingNotes.editableMeetingInfo.fields.title")}
            </Typography>
            <TextField
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                  borderRadius: "4px",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#242C63" },
                  "&.Mui-focused fieldset": { borderColor: "#242C63" },
                },
              }}
            />
          </Box>

          {/* Date picker */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.editableMeetingInfo.fields.date")}
            </Typography>
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                px: 1.5,
                py: 1,
              }}
            >
              <DateCalendar
                value={date}
                onChange={onDateChange}
                showDaysOutsideCurrentMonth
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  "& .MuiPickersCalendarHeader-root": {
                    marginTop: 0.5,
                    marginBottom: 1,
                    paddingLeft: 1,
                    paddingRight: 1,
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    color: "#242C63",
                    fontSize: "1rem",
                    fontWeight: 600,
                  },
                  "& .MuiDayCalendar-header": {
                    justifyContent: "space-between",
                    marginBottom: 0.5,
                  },
                  "& .MuiDayCalendar-weekDayLabel": {
                    color: "#64748B",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    width: 36,
                    margin: "0 2px",
                  },
                  "& .MuiPickersDay-root": {
                    color: "#242C63",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    width: 36,
                    height: 36,
                    margin: "2px",
                    borderRadius: "8px",
                  },
                  "& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth": {
                    color: "#C3C7CC",
                  },
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#242C63",
                    color: "#FFFFFF",
                  },
                  "& .MuiPickersDay-root.Mui-selected:hover": {
                    backgroundColor: "#353D7A",
                  },
                  "& .MuiPickersArrowSwitcher-button": {
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#64748B",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Time field */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.editableMeetingInfo.fields.time")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
              <TimePicker
                value={startTime}
                onChange={onStartTimeChange}
                ampm={useAmPm}
                format={useAmPm ? "hh:mm A" : "HH:mm"}
                slotProps={timePickerSlotProps}
              />
              <Typography sx={{ color: "#64748B", fontWeight: 500 }}>
                -
              </Typography>
              <TimePicker
                value={endTime}
                onChange={onEndTimeChange}
                ampm={useAmPm}
                format={useAmPm ? "hh:mm A" : "HH:mm"}
                slotProps={timePickerSlotProps}
              />
            </Box>
          </Box>

          {/* Participants field */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.editableMeetingInfo.fields.participants")}
            </Typography>
            <TextField
              value={newParticipant}
              onChange={(e) => {
                setNewParticipant(e.target.value);
                if (participantError) {
                  setParticipantError(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddParticipant();
                }
              }}
              placeholder={t(
                "meetingNotes.editableMeetingInfo.participantPlaceholder",
              )}
              fullWidth
              size="small"
              error={participantError !== null}
              helperText={
                participantError === "duplicate"
                  ? t("meetingNotes.editableMeetingInfo.duplicateEmail")
                  : undefined
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                  borderRadius: "4px",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#242C63" },
                  "&.Mui-focused fieldset": { borderColor: "#242C63" },
                },
              }} slotProps={{ input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleAddParticipant}
                      disabled={!trimmedParticipant || isDuplicate}
                      sx={{ color: "#242C63" }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              } }}
            />
            {participants.length > 0 && (
              <Box
                sx={{
                  mt: 1.25,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  p: 1,
                }}
              >
                {participants.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size="small"
                    onDelete={() => handleRemoveParticipant(p)}
                    sx={{
                      fontSize: "0.8125rem",
                      backgroundColor: "#F1F5F9",
                      color: "#1A2048",
                      "& .MuiChip-deleteIcon": {
                        color: "#94A3B8",
                        "&:hover": { color: "#DC2626" },
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB", gap: 1 }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#E5E7EB",
            color: "#1A2048",
            boxShadow: "none",
            "&:hover": { borderColor: "#C3C7CC", boxShadow: "none" },
          }}
        >
          {t("button.cancel")}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "#242C63",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#353D7A", boxShadow: "none" },
          }}
        >
          {t("button.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditableMeetingInfo;
